import React, { memo, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { InternalRoleForm } from 'src/components/internal-access/roles/InternalRoleForm'
import { InternalRoleTypeChip } from 'src/components/internal-access/roles/InternalRoleTypeChip'
import { BackButton } from 'src/components/shared/BackButton'
import { Chip } from 'src/components/shared/Chip'
import { ContentCard } from 'src/components/shared/ContentCard'
import { SectionCard } from 'src/components/shared/SectionCard'
import { SectionCardSkeleton } from 'src/components/shared/SectionCardSkeleton'
import {
  AdminCreateInternalRole,
  AdminFetchInternalRole,
  AdminUpdateInternalRole,
} from 'src/constants/actionTypes'
import { InternalAccessTab } from 'src/constants/internal-access/internalAccessTabs'
import { ADMIN_ROUTES, buildInternalRolePath } from 'src/constants/routes'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import type { InternalAccessRole, InternalRoleFormValues } from 'src/interfaces/internalAccess'
import {
  getInternalRoleDetail,
  getInternalRoleDetailError,
  getInternalRoleDetailLoading,
} from 'src/selectors/internalAccess'
import { buildRoleUpdatePayload } from 'src/utils/internalAccess/internalRoles'

/**
 * The role editor, for both `/roles/new` (no `roleId`) and `/roles/:roleId`. Fetched BY ID, never
 * plucked from the list, so a deep link works.
 */
const InternalRolePageComponent = () => {
  const { roleId } = useParams<{ roleId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const snackbar = useSnackbar()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const roleDetail = useSelector(getInternalRoleDetail)
  const isLoading = useSelector(getInternalRoleDetailLoading)
  const detailError = useSelector(getInternalRoleDetailError)

  const isCreateMode = !roleId
  // Guards against showing the previous role for a moment while the next one loads.
  const role = !isCreateMode && roleDetail?.id === roleId ? roleDetail : null

  useEffect(() => {
    if (!isAuthenticated || !roleId) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchInternalRole.request({ roleId }))
  }, [asyncDispatchWithErrorToast, isAuthenticated, roleId])

  const listPath = useMemo(() => {
    const listParams = new URLSearchParams(searchParams)

    listParams.set('tab', InternalAccessTab.Roles)

    return { pathname: ADMIN_ROUTES.USERS_MANAGEMENT_URL, search: listParams.toString() }
  }, [searchParams])

  const handleCancel = useCallback(() => {
    navigate(listPath)
  }, [listPath, navigate])

  const handleSubmit = useCallback(
    async (values: InternalRoleFormValues) => {
      if (isCreateMode) {
        const created = await asyncDispatchWithErrorToast<InternalAccessRole>(
          AdminCreateInternalRole.request({
            name: values.name.trim(),
            permissions: values.permissions,
          }),
        )

        if (!created) {
          return
        }

        snackbar?.enqueueSnackbar('Role created', { variant: 'success' })
        // Replace, so Back from the new role goes to the list rather than an empty create form.
        navigate(
          { pathname: buildInternalRolePath(created.id), search: searchParams.toString() },
          { replace: true },
        )

        return
      }

      if (!role) {
        return
      }

      const updateBody = buildRoleUpdatePayload(role, values)

      if (!updateBody) {
        return
      }

      const updated = await asyncDispatchWithErrorToast<InternalAccessRole>(
        AdminUpdateInternalRole.request({ roleId: role.id, ...updateBody }),
      )

      if (!updated) {
        return
      }

      snackbar?.enqueueSnackbar('Role updated', { variant: 'success' })
    },
    [asyncDispatchWithErrorToast, isCreateMode, navigate, role, searchParams, snackbar],
  )

  const title = isCreateMode ? 'New role' : role?.name ?? 'Role'

  return (
    <div
      data-testid="internal-role-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-y-auto p-6`}
    >
      <ContentCard className={tw`flex flex-col gap-4`}>
        <BackButton label="Back to roles" to={listPath} dataTestId="internal-role-back-button" />
        <div css={tw`flex flex-row flex-wrap items-center gap-3`}>
          <Typography variant="h2">{title}</Typography>
          {role ? <InternalRoleTypeChip role={role} /> : null}
          {role?.allPermissions ? <Chip text="All permissions" /> : null}
        </div>
      </ContentCard>

      {!isCreateMode && isLoading && !role ? <SectionCardSkeleton /> : null}

      {!isCreateMode && detailError && !role ? (
        <SectionCard title="Role">
          <Typography variant="body2" css={tw`text-critical-500`}>
            {detailError}
          </Typography>
        </SectionCard>
      ) : null}

      {isCreateMode || role ? (
        <InternalRoleForm role={role} onSubmit={handleSubmit} onCancel={handleCancel} />
      ) : null}
    </div>
  )
}

export const InternalRolePage = memo(InternalRolePageComponent)
